<script setup lang="ts">
import { tokenService } from '~/api/service/token.service'
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import { useResponsiveScreen } from '~/composables/useResponsiveScreen'
import { TOKEN_TYPE_NAME, TETHER_USDT_TOKEN, NATIVE_TOKEN } from '~/constants/token.constants'
import { ITokenType } from '~/types/token-type.type'
import BcLabelContainer from '~/ui/LabelContainer/BcLabelContainer.vue'

const route = useRoute()
const { isMobile } = useResponsiveScreen()

const address = route.params.address as string
const isUsdt = computed(() => {
  const addr = address.toLowerCase()
  return addr === TETHER_USDT_TOKEN.address.toLowerCase()
})

const { data } = tokenService.useGetTokenDetail(address, ITokenType.ERC20)
</script>

<template>
  <div
    v-if="data"
    class="summary-container"
  >
    <div class="info-container">
      <Chip
        class="w-fit text-base"
        offUpperCase
      >
        <div class="flex items-center gap-x-1 font-bold">
          <FontAwesomeIcon icon="coins" />
          <span>{{ TOKEN_TYPE_NAME[data.tokenType] }}</span>
        </div>
      </Chip>
      <div class="mb-4 mt-2 flex w-full items-center gap-x-4">
        <img
          :src="data.tokenInfo.logo"
          :alt="`${data.tokenInfo.name} symbol image`"
          width="48"
          height="48"
          class="rounded-full"
        >
        <p class="token-name">
          {{ data.tokenInfo.name }}
          <span v-if="data.tokenInfo.symbol">({{ data.tokenInfo.symbol }})</span>
        </p>
      </div>

      <div class="token-contract">
        <AddressCopyLink
          :address="data.tokenInfo.address"
          class="truncate"
          :line-break="isMobile"
        />
      </div>
    </div>

    <div class="info-container">
      <div class="total-container">
        <BcLabelContainer
          v-if="data.price"
          label="Price (24H)"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="'$ ' + setComma(data.price)"
            >{{
              '$ ' + (isMobile ? setCommaWithDigits(data.price, 2) : setComma(data.price))
            }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          v-if="data.marketCap"
          label="Market Cap"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="'$ ' + setComma(data.marketCap)"
            >{{
              '$ ' + (isMobile ? setAbbr(data.marketCap) : setComma(data.marketCap))
            }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          v-if="data.fdv"
          label="FDV"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="'$ ' + setComma(data.fdv)"
            >{{
              '$ ' + (isMobile ? setAbbr(data.fdv) : setComma(data.fdv))
            }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          v-if="data.tokenInfo.symbol === NATIVE_TOKEN.symbol || isUsdt"
          label="Total Supply"
          label-size="base"
          gap-y="gap-y-1"
          :tooltip="data.tokenInfo.symbol === NATIVE_TOKEN.symbol ?`Total supply is calculated by subtracting the amount used in NXPC Fission from the fixed max supply of 1 billion tokens.
          Tokens on BNB Smart Chain and Avalanche C-Chain are bridged representations of existing supply, not newly minted tokens.` : null"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="data.totalSupply"
            >{{
              isMobile ? data.totalSupplyAbbr : data.totalSupply
            }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          label="Total Transfers"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="data.totalTransfers"
            >{{
              isMobile ? data.totalTransfersAbbr : data.totalTransfers
            }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          label="Holders"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="data.totalHolders"
            >{{ isMobile ? data.totalHoldersAbbr : data.totalHolders }}</span>
          </template>
        </BcLabelContainer>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.summary-container {
  @apply grid grid-cols-2 text-primary/40 gap-x-4;

  @include sm {
    @apply grid-cols-1 gap-y-4;
  }

  .info-container {
    @apply flex flex-col;

    .token-name {
      @apply font-bold text-3xl leading-tight truncate text-primary flex items-center gap-x-1;

      @include sm {
        @apply text-2xl;
      }
    }

    .total-container {
      @apply grid grid-cols-3 gap-x-1 gap-y-4;
    }

    .token-info {
      @apply flex items-center gap-x-1 text-lg leading-normal text-primary max-w-fit flex-wrap break-all;
    }
  }
}
</style>
